/**
 * @file useBlockActions.ts
 * @description Действия с блоками открытой страницы конструктора
 * (phase19.16 Р1-6а; Р2 — секции v2, контракт §16.2, uiux-design-r2 §6, §9):
 * порядок, показ, удаление с подтверждением, правка текста с канвы,
 * сохранение формы блока. Вставка — `useBlockInserts`. Каждое действие
 * кладёт запись в «Отменить» и показывает всплывашку с [Отменить].
 *
 * Правки блока идут с `expectedUpdatedAt` из ячейки страницы
 * (`BlockCells`): 409 `block_changed` — понятный текст и свежая страница,
 * правки в форме остаются. Текст с канвы для блока, открытого в панели с
 * несохранёнными правками, уходит в форму (`formSink`), а не на сервер.
 */

import { arrayMove } from "@dnd-kit/sortable";
import type { SectionPayload } from "@chaiberi/shared";
import { useCallback, useEffect, useMemo, useRef, type MutableRefObject } from "react";

import type { BuilderPagesApi } from "./useBuilderPages";
import { useBlockInserts, type BlockInserts } from "./useBlockInserts";
import type { UndoApi } from "./useUndoStack";
import { blockTitle, isSectionBlock } from "../../components/site-pages/block-meta";
import { useCanvasFontRegistry } from "../../components/site-view/shop-fonts-context";
import type { ConfirmRequest } from "../../components/ui/use-confirm";
import { ApiError } from "../../lib/api-client";
import { sitePages, type BlockAppearance, type SavedSiteBlock, type SitePageBlock } from "../../lib/api-site-pages";
import { blockErrorText } from "../../lib/site-builder/block-errors";
import { createBlockCells, editEntry, patchByRef, removedEntry, reorderEntry, snapshotOf, toggleEntry, type HistoryEnv } from "../../lib/site-builder/block-undo";
import { GUIDE_CANVAS_TEXT_KEY, writeFlag } from "../../lib/site-builder/first-run";
import { applyCanvasText } from "../../lib/site-builder/section-edit";
import { BLOCK_ACTION_TEXT, sectionContextOf } from "../../lib/site-builder/template-rules";

/** Правка текста с канвы: путь поля и новый текст. */
export interface SectionCanvasEdit {
  path: string;
  value: string;
}

/** Форма блока, открытая в панели: правки с канвы идут в неё. */
export interface FormSink {
  blockId: string;
  dirty: boolean;
  applyCanvasText: (path: string, value: string) => boolean;
  /** Id элемента по пути канвы — по payload формы (канва рисует несохранённую форму). */
  elementIdAt: (path: string) => string | null;
}

interface Deps {
  pages: BuilderPagesApi;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  notify: {
    error: (text: string) => void;
    success: (text: string, options?: { action?: { label: string; onClick: () => void } }) => void;
  };
  undo: UndoApi;
  /** Снять выбор блока (удалён или пересоздан). */
  onBlockGone: (id: string) => void;
  formSink: MutableRefObject<FormSink | null>;
}

export interface BlockActions extends BlockInserts {
  reorder: (next: SitePageBlock[]) => Promise<void>;
  move: (block: SitePageBlock, dir: -1 | 1) => Promise<void>;
  toggle: (block: SitePageBlock, isEnabled: boolean) => Promise<void>;
  remove: (block: SitePageBlock) => Promise<void>;
  canvasEdit: (block: SitePageBlock, edit: SectionCanvasEdit) => Promise<void>;
  /** Сохранить форму блока (с `expectedUpdatedAt`) и записать в «Отменить»; ошибки — вызывающему. */
  saveBlock: (block: SitePageBlock, next: { payload: SectionPayload; appearance: BlockAppearance | null }) => Promise<SavedSiteBlock>;
}

export function useBlockActions({ pages, confirm, notify, undo, onBlockGone, formSink }: Deps): BlockActions {
  const { page, setPage, reloadPage, bumpCanvas } = pages;
  const pageId = page?.id ?? null;
  // Тексты по месту блока: шаблон товара (SB61), страница и место (Р3а) — все в черновике.
  const texts = BLOCK_ACTION_TEXT[sectionContextOf(page?.kind)];
  // Одна таблица ячеек на открытую страницу (id → ячейка), свежий порядок — для истории.
  const cells = useMemo(() => createBlockCells(), [pageId]); // eslint-disable-line react-hooks/exhaustive-deps
  const latestPage = useRef(page);
  latestPage.current = page;
  useEffect(() => {
    if (page) cells.sync(page.blocks);
  }, [cells, page]);

  const refresh = useCallback(async () => {
    await reloadPage();
    bumpCanvas();
  }, [bumpCanvas, reloadPage]);
  // Блоки из ответа сервера — в состояние страницы и ячейки: перестановка
  // переписывает все блоки страницы (новый updatedAt), сохранение — один.
  const applyBlocks = useCallback(
    (fresh: readonly SitePageBlock[]) => {
      cells.sync(fresh);
      const current = latestPage.current;
      if (!current || fresh.length === 0) return;
      const byId = new Map(fresh.map((b) => [b.id, b]));
      const whole = fresh.length === current.blocks.length && current.blocks.every((b) => byId.has(b.id));
      const blocks = whole ? [...fresh] : current.blocks.map((b) => byId.get(b.id) ?? b);
      const next = { ...current, blocks };
      latestPage.current = next;
      setPage(next);
    },
    [cells, setPage],
  );
  // Свежий реестр шрифтов для записей истории (пересоздание блока без удалённых шрифтов, SB120 этап 3а).
  const fontRegistry = useCanvasFontRegistry();
  const fontRef = useRef(fontRegistry);
  useEffect(() => {
    fontRef.current = fontRegistry;
  }, [fontRegistry]);
  const env = useMemo<HistoryEnv | null>(
    () => (pageId ? { pageId, cells, refresh, applyBlocks, currentOrder: () => latestPage.current?.blocks.map((b) => b.id) ?? [], fontRegistry: () => fontRef.current } : null),
    [applyBlocks, cells, pageId, refresh],
  );
  const ref = useCallback((block: SitePageBlock) => cells.refFor(block.id, block.updatedAt), [cells]);

  const handleError = useCallback(
    (err: unknown, fallback: string, blockId?: string) => {
      const gone = err instanceof ApiError && err.code === "site_block_not_found";
      if (gone && blockId) onBlockGone(blockId);
      if (gone || (err instanceof ApiError && err.code === "block_changed")) void refresh();
      notify.error(gone ? "Блоки страницы изменились в другом месте — список обновлён." : blockErrorText(err, fallback));
    },
    [notify, onBlockGone, refresh],
  );
  const toastUndo = useCallback((text: string) => notify.success(text, { action: { label: "Отменить", onClick: () => void undo.undo() } }), [notify, undo]);

  const inserts = useBlockInserts({ page, env, refresh, handleError, toastUndo, undo });

  const reorder = useCallback(
    async (next: SitePageBlock[]) => {
      if (!page || !env) return;
      setPage({ ...page, blocks: next });
      try {
        applyBlocks(await sitePages.reorderBlocks(page.id, next.map((b) => b.id)));
        bumpCanvas();
        undo.push(reorderEntry(env, "Порядок блоков", page.blocks.map((b) => b.id), next.map((b) => b.id)));
      } catch (err) {
        setPage(page);
        handleError(err, "Не удалось изменить порядок блоков.");
      }
    },
    [applyBlocks, bumpCanvas, env, handleError, page, setPage, undo],
  );

  const move = useCallback(
    async (block: SitePageBlock, dir: -1 | 1) => {
      const from = page?.blocks.findIndex((b) => b.id === block.id) ?? -1;
      const to = from + dir;
      if (!page || from < 0 || to < 0 || to >= page.blocks.length) return;
      await reorder(arrayMove(page.blocks, from, to));
    },
    [page, reorder],
  );

  const toggle = useCallback(
    async (block: SitePageBlock, isEnabled: boolean) => {
      if (!page || !env) return;
      setPage({ ...page, blocks: page.blocks.map((b) => (b.id === block.id ? { ...b, isEnabled } : b)) });
      try {
        const cell = ref(block);
        applyBlocks([await patchByRef(cell, { isEnabled })]);
        bumpCanvas();
        const label = `${isEnabled ? "Показан" : "Скрыт"} блок «${blockTitle(block)}»`;
        undo.push(toggleEntry(env, label, cell, isEnabled));
        if (!isEnabled) toastUndo(`${label} — ${texts.hidden}`);
      } catch (err) {
        setPage(page);
        handleError(err, isEnabled ? "Не удалось показать блок." : "Не удалось скрыть блок.", block.id);
      }
    },
    [applyBlocks, bumpCanvas, env, handleError, page, ref, setPage, texts, toastUndo, undo],
  );

  const remove = useCallback(
    async (block: SitePageBlock) => {
      if (!page || !env) return;
      const title = blockTitle(block);
      const snap = snapshotOf(block);
      const ok = await confirm({
        title: `Удалить блок «${title}»?`,
        description: snap ? texts.removeBody : "Он уберётся из черновика страницы, на сайте — после «Опубликовать». Это прежний блок — вернуть его кнопкой «Отменить» нельзя.",
        action: "Удалить",
        danger: true,
      });
      if (!ok) return;
      const index = page.blocks.findIndex((b) => b.id === block.id);
      const afterId = index > 0 ? (page.blocks[index - 1]?.id ?? null) : null;
      try {
        const cell = ref(block);
        await sitePages.removeBlock(cell.id);
        onBlockGone(block.id);
        await refresh();
        if (snap) {
          undo.push(removedEntry(env, `Удалён блок «${title}»`, cell, snap, afterId));
          toastUndo(`Блок «${title}» удалён`);
        }
      } catch (err) {
        handleError(err, "Не удалось удалить блок.", block.id);
      }
    },
    [confirm, env, handleError, onBlockGone, page, ref, refresh, texts, toastUndo, undo],
  );

  const saveBlock = useCallback(
    async (block: SitePageBlock, next: { payload: SectionPayload; appearance: BlockAppearance | null }) => {
      const cell = ref(block);
      const saved = await patchByRef(cell, next);
      if (env) undo.push(editEntry(env, `Правка блока «${blockTitle(block)}»`, cell, { payload: block.payload as SectionPayload, appearance: block.appearance }, next));
      return saved;
    },
    [env, ref, undo],
  );

  // Канва уже показывает новый текст — iframe не перерисовываем.
  const canvasEdit = useCallback(
    async (block: SitePageBlock, edit: SectionCanvasEdit) => {
      if (!isSectionBlock(block)) return;
      const sink = formSink.current;
      if (sink && sink.blockId === block.id && sink.dirty) {
        sink.applyCanvasText(edit.path, edit.value);
        return;
      }
      const payload = applyCanvasText(block.payload as SectionPayload, edit.path, edit.value);
      if (!payload) return;
      try {
        await saveBlock(block, { payload, appearance: block.appearance });
        writeFlag(GUIDE_CANVAS_TEXT_KEY, true);
        await reloadPage();
      } catch (err) {
        handleError(err, "Не удалось сохранить текст.", block.id);
        bumpCanvas(); // откат канвы к сохранённому
      }
    },
    [bumpCanvas, formSink, handleError, reloadPage, saveBlock],
  );

  return { ...inserts, reorder, move, toggle, remove, canvasEdit, saveBlock };
}
